<template>
  <div class="color-selector" data-test-id="color-palette-input">
    <canvas
      ref="colorCanvas"
      :width="canvas.width"
      :height="canvas.height"
      @pointerdown="startPicking"
      @pointermove="pickColor"
      @pointerup="stopPicking"
      @pointerleave="stopPicking"
      :style="{ height: `${canvas.height}px`, width: `${canvas.width}px` }"
      data-test-id="color-selector-canvas"
    ></canvas>
    <div class="slider-container" :style="`--thumb-size: ${thumbSize}px;`">
      <div class="color-preview" :style="`background-color: ${color.hex};`" data-test-id="color-selector-preview"></div>
      <input
        class="hue-slider"
        type="range"
        min="0"
        max="360"
        :value="color.hsv.hue"
        @input="setHue"
        :style="`--thumb-background: hsl(${color.hsv.hue}, 100%, 50%);`"
        data-test-id="color-selector-hue-input"
      />
    </div>
    <div class="rgb-container">
      <label
        >R
        <input
          type="number"
          min="0"
          max="255"
          :value="color.rgb.red"
          @input="(event: InputEvent) => setSingleRGBValue('red', event)"
          placeholder="255"
          data-test-id="color-selector-red"
        />
      </label>

      <label
        >G
        <input
          type="number"
          min="0"
          max="255"
          :value="color.rgb.green"
          @input="(event: InputEvent) => setSingleRGBValue('green', event)"
          placeholder="255"
          data-test-id="color-selector-green"
        />
      </label>

      <label
        >B
        <input
          type="number"
          id="red"
          min="0"
          max="255"
          :value="color.rgb.blue"
          @input="(event: InputEvent) => setSingleRGBValue('blue', event)"
          placeholder="255"
          data-test-id="color-selector-blue"
        />
      </label>
    </div>
    <div class="action-container">
      <Btn color="blue" @click="$emit('setColor', color.hex)" data-test-id="color-selector-apply-button">
        {{ $t('action.apply') }}
      </Btn>
      <Btn color="grey" @click="$emit('close', color.hex)" data-test-id="color-selector-cancel-button">
        {{ $t('action.cancel') }}
      </Btn>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { clamp } from 'ramda';

type RGB = { red: number; green: number; blue: number };
type HSV = { hue: number; saturation: number; value: number };
type Color = { hex: string; rgb: RGB; hsv: HSV };
type CanvasCoords = { xPosition: number; yPosition: number };

const defaultHex = '#ff0000';

export default defineComponent({
  name: 'ColorSelector',
  emits: { setColor: null, close: null },

  props: {
    modelValue: {
      type: String,
      default: defaultHex,
    },
  },

  data(): {
    color: {
      hex: string;
      rgb: RGB;
      hsv: HSV;
    };
    isPicking: boolean;
    canvas: {
      height: number;
      width: number;
      ctx: CanvasRenderingContext2D;
      lastMousePosition: CanvasCoords;
    };
    thumbSize: number;
  } {
    const hexValue = this.modelValue ?? defaultHex;
    const rgbValue: RGB = this.hexToRgb(hexValue) ?? ({ red: 255, green: 0, blue: 0 } satisfies RGB); // Default to #ff0000
    const hsvValue: HSV = this.rgbToHsv(rgbValue);

    return {
      color: {
        hex: hexValue,
        rgb: rgbValue,
        hsv: hsvValue,
      },
      isPicking: false,
      canvas: {
        height: 180,
        width: 280,
        ctx: null,
        lastMousePosition: {
          xPosition: 0,
          yPosition: 0,
        },
      },
      thumbSize: 25,
    };
  },

  mounted() {
    this.canvas.lastMousePosition = this.hsvToCanvasCoords(this.color.hsv);
    this.canvas.ctx = this.$refs.colorCanvas.getContext('2d');

    this.$watch(
      () => [this.color, this.canvas.lastMousePosition],
      () => {
        this.drawGradient();
        this.drawCircle(
          this.canvas.lastMousePosition.xPosition,
          this.canvas.lastMousePosition.yPosition,
          this.color.hex
        );
      },
      { deep: true, immediate: true }
    );
  },

  methods: {
    drawGradient() {
      const saturationGradient = this.canvas.ctx.createLinearGradient(0, 0, this.canvas.width, 0);
      saturationGradient.addColorStop(0, 'white');
      saturationGradient.addColorStop(1, `hsl(${this.color.hsv.hue}, 100%, 50%)`);
      this.canvas.ctx.fillStyle = saturationGradient;
      this.canvas.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

      const brightnessGradient = this.canvas.ctx.createLinearGradient(0, 0, 0, this.canvas.height);
      brightnessGradient.addColorStop(0, 'rgba(0,0,0,0)');
      brightnessGradient.addColorStop(1, 'rgba(0,0,0,1)');
      this.canvas.ctx.fillStyle = brightnessGradient;
      this.canvas.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    },
    drawCircle(x: number, y: number, hexValue: string) {
      const lineWidth = 2;

      this.canvas.ctx.beginPath();
      this.canvas.ctx.arc(x, y, this.thumbSize / 2, 0, Math.PI * 2);
      this.canvas.ctx.strokeStyle = 'black';
      this.canvas.ctx.lineWidth = lineWidth;
      this.canvas.ctx.stroke();

      this.canvas.ctx.beginPath();
      this.canvas.ctx.arc(x, y, this.thumbSize / 2 - lineWidth, 0, 2 * Math.PI);
      this.canvas.ctx.fillStyle = hexValue;
      this.canvas.ctx.fill();
      this.canvas.ctx.lineWidth = lineWidth;
      this.canvas.ctx.strokeStyle = 'white';
      this.canvas.ctx.stroke();
    },
    rgbToHex({ red, green, blue }: RGB): string {
      const toHex = (component: number) => {
        const hex = component.toString(16);

        return hex.length === 1 ? `0${hex}` : hex;
      };

      return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
    },
    hexToRgb(hex: string): RGB | null {
      // Remove the hash symbol if present
      const strippedHex = hex.replace(/^#/, '');

      // Check if the hex code is valid
      if (strippedHex.length !== 6) return null;

      const red = parseInt(strippedHex.substring(0, 2), 16);
      const green = parseInt(strippedHex.substring(2, 4), 16);
      const blue = parseInt(strippedHex.substring(4, 6), 16);

      if (isNaN(red) || isNaN(green) || isNaN(blue)) return null;

      return { red, green, blue };
    },
    rgbToHsv({ red, green, blue }: RGB): HSV {
      // For more information, see: https://en.wikipedia.org/wiki/HSL_and_HSV#From_RGB

      const normalizedRed = red / 255;
      const normalizedGreen = green / 255;
      const normalizedBlue = blue / 255;

      const value = Math.max(normalizedRed, normalizedGreen, normalizedBlue);
      const minimumComponent = Math.min(normalizedRed, normalizedGreen, normalizedBlue);
      const chroma = value - minimumComponent;
      const saturation = value === 0 ? 0 : chroma / value;

      let hue = -1;
      if (chroma === 0) {
        hue = 0;
      } else if (value === normalizedRed) {
        hue = 60 * (((normalizedGreen - normalizedBlue) / chroma) % 6);
      } else if (value === normalizedGreen) {
        hue = 60 * ((normalizedBlue - normalizedRed) / chroma + 2);
      } else if (value === normalizedBlue) {
        hue = 60 * ((normalizedRed - normalizedGreen) / chroma + 4);
      }

      return {
        hue: Math.round(hue < 0 ? hue + 360 : hue),
        saturation: Math.round(saturation * 100),
        value: Math.round(value * 100),
      };
    },
    // eslint-disable-next-line max-statements
    hsvToRgb({ hue, saturation, value }: HSV): RGB {
      // For more information, see: https://en.wikipedia.org/wiki/HSL_and_HSV#HSV_to_RGB

      const normalizedSaturation = saturation / 100;
      const normalizedValue = value / 100;

      const chroma = normalizedValue * normalizedSaturation;
      const secondLargestComponent = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
      const matchedValue = normalizedValue - chroma;

      let red = 0;
      let green = 0;
      let blue = 0;

      if (hue >= 0 && hue < 60) {
        red = chroma;
        green = secondLargestComponent;
        blue = 0;
      } else if (hue >= 60 && hue < 120) {
        red = secondLargestComponent;
        green = chroma;
        blue = 0;
      } else if (hue >= 120 && hue < 180) {
        red = 0;
        green = chroma;
        blue = secondLargestComponent;
      } else if (hue >= 180 && hue < 240) {
        red = 0;
        green = secondLargestComponent;
        blue = chroma;
      } else if (hue >= 240 && hue < 300) {
        red = secondLargestComponent;
        green = 0;
        blue = chroma;
      } else if (hue >= 300 && hue <= 360) {
        red = chroma;
        green = 0;
        blue = secondLargestComponent;
      }

      return {
        red: Math.round((red + matchedValue) * 255),
        green: Math.round((green + matchedValue) * 255),
        blue: Math.round((blue + matchedValue) * 255),
      };
    },
    hsvToCanvasCoords({ saturation, value }: HSV): CanvasCoords {
      const width = this.canvas.width;
      const height = this.canvas.height;

      const x = (saturation / 100) * width;
      const y = ((100 - value) / 100) * height; // Inverting yPosition to match canvas coordinates

      return { xPosition: x, yPosition: y };
    },
    /**
     * Set hue from range slider
     */
    setHue(event: InputEvent) {
      this.color.hsv.hue = (event.target as HTMLInputElement).valueAsNumber;

      const rgbValue = this.hsvToRgb(this.color.hsv);

      const hexValue = this.rgbToHex(rgbValue);

      if (JSON.stringify(this.color.hex) !== JSON.stringify(hexValue)) {
        this.color.hex = hexValue;
      }

      if (JSON.stringify(this.color.rgb) !== JSON.stringify(rgbValue)) {
        this.color.rgb = rgbValue;
      }

      this.canvas.lastMousePosition = this.hsvToCanvasCoords(this.color.hsv);
    },

    /**
     * set an RGB value from a single input
     */
    setSingleRGBValue(part: 'red' | 'green' | 'blue', event: InputEvent) {
      const target = event.target as HTMLInputElement;

      if (!Number.isNaN(target.valueAsNumber)) {
        // clamp since user can enter values lower and higher manually
        this.color.rgb[part] = clamp(0, 255, target.valueAsNumber);

        const hexValue = this.rgbToHex(this.color.rgb);
        const hsvValue = this.rgbToHsv(this.color.rgb);

        if (JSON.stringify(this.color.hex) !== JSON.stringify(hexValue)) {
          this.color.hex = hexValue;
        }

        if (JSON.stringify(this.color.hsv) !== JSON.stringify(hsvValue)) {
          this.color.hsv = hsvValue;
          this.canvas.lastMousePosition = this.hsvToCanvasCoords(hsvValue);
        }
      }
    },
    /**
     * Pick color from canvas gradient
     */
    pickColorFromPosition(xPosition: number, yPosition: number) {
      // Redraw the gradient so color can be picked
      this.drawGradient();

      const rect = this.$refs.colorCanvas.getBoundingClientRect();

      const clampedX = Math.max(0, Math.min(xPosition - rect.left, this.canvas.width - 1));
      const clampedY = Math.max(0, Math.min(yPosition - rect.top, this.canvas.height - 1));

      this.canvas.lastMousePosition.xPosition = clampedX;
      this.canvas.lastMousePosition.yPosition = clampedY;

      const imageData = this.canvas.ctx.getImageData(clampedX, clampedY, 1, 1).data;

      const rgbValue: RGB = { red: imageData[0], green: imageData[1], blue: imageData[2] } satisfies RGB;
      const hexValue = this.rgbToHex(rgbValue);
      const hsvValue = this.rgbToHsv(rgbValue);

      this.color = {
        hex: hexValue,
        rgb: rgbValue,
        hsv: {
          ...hsvValue,
          hue: this.color.hsv.hue,
        },
      } satisfies Color;
    },
    pickColor(event: MouseEvent) {
      if (!this.isPicking) return;

      this.pickColorFromPosition(event.clientX, event.clientY);
    },
    startPicking(event: MouseEvent) {
      this.isPicking = true;
      this.pickColor(event);
    },
    stopPicking() {
      this.isPicking = false;
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
$gap: 8px;

@mixin color-track {
  background: linear-gradient(
    to right,
    #ff0000 0%,
    #ffff00 17%,
    #00ff00 33%,
    #00ffff 50%,
    #0000ff 67%,
    #ff00ff 83%,
    #ff0000 100%
  );
  height: 18px;
}

.color-selector {
  overflow: hidden;
  width: 300px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: $gap;
}

.slider-container {
  display: flex;
  gap: $gap;

  & > .color-preview {
    height: calc(2 * var(--thumb-size));
    width: calc(2 * var(--thumb-size));
    border-radius: 50%;
    flex: 1 0 auto;
  }

  & > input[type='range'] {
    appearance: none;
    background: transparent;
    cursor: pointer;
    width: 15rem;

    /* Chrome, Safari, Opera, and Edge Chromium */
    &::-webkit-slider-runnable-track,
    &:focus::-webkit-slider-runnable-track {
      @include color-track;
    }

    /* Firefox */
    &::-moz-range-track,
    &:focus::-moz-range-track {
      @include color-track;
    }

    &::-webkit-slider-thumb {
      -webkit-appearance: none; /* Override default look */
      appearance: none;
      background-color: var(--thumb-background);
      height: var(--thumb-size);
      width: var(--thumb-size);
      border-radius: 50%;
      border: 4px white solid;
      box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.5);
    }
  }
}

.rgb-container {
  display: flex;
  justify-content: space-between;
  text-align: center;
  gap: $gap;

  input {
    height: 28px;
    color: colors.$color-grey-800;
    border: none;
    background: colors.$color-grey-100;
    box-shadow: settings.$box-shadow-border-grey-50;
    border-radius: settings.$border-radius;
    padding: 0 8px 1px 8px;

    &::placeholder {
      color: colors.$color-placeholder;
    }
  }

  label {
    font-size: 16px;
  }
}

.action-container {
  display: flex;
  justify-content: start;
  gap: $gap;
  margin-top: 8px;
}
</style>
